import { BearingEnvelope, CeilingPlane, RoofPlane, RoofPlaneAbsoluteGeometry } from '@paragon/contract-library';
import { Point2D } from '@paragon/geometry-library';
import { notNullish } from '@paragon/utilities';

import { isInteractiveEditActive } from '../../selectors/interactiveEditingSelectors';
import { getPickingObjectInfo } from '../../selectors/pickingSelectors';
import { getCalculatedRoofAndCeilingPlanes } from '../../selectors/roofAndCeilingPlaneSelectors';
import { hoveredPlaneClearedDuringPlanePicking } from '../../slices/applicationSlice';
import { layoutSlice } from '../../slices/layoutSlice';
import { AsyncThunkActionVoid, ThunkActionVoid } from '../thunkActions';
import { editCeilingPlanes } from './ceilingPlaneActions';
import { editRoofPlanes } from './roofPlaneActions';

const errorMessage = (guid: string) => `Unable to find plane '${guid}'—does not match a loaded roof or ceiling plane.`;

export const highlightUnknownTypePlaneByGuid =
  (guid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const { ceilingPlanes, roofPlanes } = getState().layout;

    if (guid in ceilingPlanes) {
      dispatch(layoutSlice.actions.highlightedCeilingPlaneSet(guid));
    } else if (guid in roofPlanes) {
      dispatch(layoutSlice.actions.highlightedRoofPlaneSet(guid));
    } else {
      throw new Error(errorMessage(guid));
    }
  };

export const setHoveredPlaneStateIfApplicable =
  (planeGuid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    if (isInteractiveEditActive(state)) return;

    const { hoveredRoofFace } = state.layout;
    if (hoveredRoofFace?.type !== 'roofOrCeilingPlane' || hoveredRoofFace.guid !== planeGuid) {
      dispatch(layoutSlice.actions.hoveredPlaneSet(planeGuid));
    }
  };

export const clearHoveredPlaneStatesIfActive =
  (planeGuid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    if (isInteractiveEditActive(state)) return;

    if (
      state.layout.hoveredRoofFace?.type === 'roofOrCeilingPlane' &&
      state.layout.hoveredRoofFace.guid === planeGuid
    ) {
      dispatch(layoutSlice.actions.hoveredRoofFaceCleared());
    }

    const pickingObjectInfo = getPickingObjectInfo(state);

    if (
      pickingObjectInfo?.type === 'pickingPlane' &&
      pickingObjectInfo.hoveredPlane != null &&
      pickingObjectInfo.hoveredPlane.type !== 'absoluteVertical'
    ) {
      dispatch(hoveredPlaneClearedDuringPlanePicking());
    }
  };

/** Any plane which depends on one of the passed bearings will be updated with new, equivalent absolute geometry. */
export const disassociatePlanesFromBearings =
  (bearings: readonly BearingEnvelope[]): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const produceEditInstructions = (plane: RoofPlane | CeilingPlane) => {
      const planeBearingGuid = plane.geometry.type === 'BearingEnvelope' ? plane.geometry.bearingEnvelopeGuid : null;
      const bearing = bearings.find(({ guid }) => guid === planeBearingGuid);
      if (!bearing) return null;
      const { leftPoint, rightPoint, top } = bearing;

      const planeDefinitionLine = getCalculatedRoofAndCeilingPlanes(getState())[plane.guid]?.calculatedGeometry.line;
      const geometry: RoofPlaneAbsoluteGeometry = {
        type: 'Absolute',
        segment: {
          basePoint: planeDefinitionLine
            ? Point2D.fromObject(leftPoint).projectOntoLine(planeDefinitionLine).toObject()
            : leftPoint,
          endPoint: planeDefinitionLine
            ? Point2D.fromObject(rightPoint).projectOntoLine(planeDefinitionLine).toObject()
            : rightPoint,
        },
        elevation: top,
        isInfinite: false,
      };
      return { guid: plane.guid, editedProperties: { geometry } };
    };

    const ceilingEditInstructions = Object.values(getState().layout.ceilingPlanes)
      .map(produceEditInstructions)
      .filter(notNullish);

    if (ceilingEditInstructions.length !== 0) await dispatch(editCeilingPlanes(ceilingEditInstructions));

    const roofEditInstructions = Object.values(getState().layout.roofPlanes)
      .map(produceEditInstructions)
      .filter(notNullish);

    if (roofEditInstructions.length !== 0) await dispatch(editRoofPlanes(roofEditInstructions));
  };
